<template>
  <div class="w-full space-y-1">
    <div class="flex items-center space-x-2">
      <input
        type="range"
        :min="sliderMin"
        :max="sliderMax"
        :step="sliderStep"
        :value="displayValue"
        @input="onSlider"
        :disabled="disabled"
        class="flex-1 disabled:opacity-50"
      />
      <input
        type="number"
        :value="displayValue"
        @input="onNumberInput"
        :min="minDisplayValue"
        :step="numberStep"
        :disabled="disabled"
        class="w-24 border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
      />
      <select
        :value="unit"
        @change="onUnitChange"
        :disabled="disabled"
        class="w-20 border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
      >
        <option value="KB">KB</option>
        <option value="MB">MB</option>
        <option value="GB">GB</option>
      </select>
    </div>
    <div v-if="modelValue" class="text-xs text-gray-500 dark:text-gray-400">
      = {{ modelValue.toLocaleString() }} KB
    </div>
  </div>
</template>

<script>
// Internal storage is KB (matches dumpcap -b filesize:N). Display is
// unit-aware. Slider ranges scale per unit so the control feels natural
// at every zoom level; the user can always type an exact number.
const UNIT_KB = { KB: 1, MB: 1024, GB: 1024 * 1024 };

function pickUnit(kb) {
  if (!kb) return "MB";
  if (kb >= UNIT_KB.GB) return "GB";
  if (kb >= UNIT_KB.MB) return "MB";
  return "KB";
}

const SLIDER = {
  KB: { min: 1024, max: 1048576, step: 1024 },      // 1 MB → 1 GB in KB
  MB: { min: 1, max: 1024, step: 1 },                 // 1 MB → 1 GB in MB
  GB: { min: 1, max: 100, step: 1 },                  // 1 GB → 100 GB
};

export default {
  name: "FilesizeInput",
  props: {
    modelValue: { type: [Number, null], default: null },
    disabled: { type: Boolean, default: false },
  },
  emits: ["update:modelValue"],
  data() {
    return { unit: pickUnit(this.modelValue) };
  },
  computed: {
    displayValue() {
      if (this.modelValue == null) return 0;
      return Math.round(this.modelValue / UNIT_KB[this.unit]);
    },
    sliderMin() { return SLIDER[this.unit].min; },
    sliderMax() { return SLIDER[this.unit].max; },
    sliderStep() { return SLIDER[this.unit].step; },
    numberStep() { return SLIDER[this.unit].step; },
    minDisplayValue() {
      // Always allow at least 1 MB worth; in KB that's 1024, in MB/GB that's 1.
      return this.unit === "KB" ? 1024 : 1;
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal != null) this.unit = pickUnit(newVal);
    },
  },
  methods: {
    onSlider(e) { this.commit(parseInt(e.target.value, 10)); },
    onNumberInput(e) {
      const raw = e.target.value;
      if (raw === "") return this.$emit("update:modelValue", null);
      const n = parseInt(raw, 10);
      if (isNaN(n) || n < 1) return;
      this.commit(n);
    },
    onUnitChange(e) {
      const newUnit = e.target.value;
      // Keep the numeric display value, reinterpret in the new unit. This
      // matches Wireshark's behaviour and avoids surprise jumps.
      const current = this.displayValue || this.minDisplayValue;
      this.unit = newUnit;
      this.commit(current);
    },
    commit(displayNum) {
      const kb = displayNum * UNIT_KB[this.unit];
      this.$emit("update:modelValue", kb);
    },
  },
};
</script>
